/**
 * Compact overlay shown while the LLM structures a note.
 */

import React from 'react';
import { Spin, Typography, theme } from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface StructureNoteLoadingOverlayProps {
  visible: boolean;
}

/**
 * Light overlay with a small spinner during AI structuring.
 */
export function StructureNoteLoadingOverlay({ visible }: StructureNoteLoadingOverlayProps) {
  const { token } = theme.useToken();
  const { t } = useTranslation('common');

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={t('structuredNote.loading.title')}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `color-mix(in srgb, ${token.colorBgContainer} 55%, transparent)`,
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        borderRadius: token.borderRadiusLG,
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: token.marginXS,
          padding: `${token.paddingXS}px ${token.paddingSM}px`,
          background: token.colorBgElevated,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowTertiary,
        }}
      >
        <Spin
          size="small"
          indicator={
            <RobotOutlined
              spin
              style={{ fontSize: 14, color: token.colorPrimary }}
            />
          }
        />
        <Text type="secondary" style={{ fontSize: token.fontSizeSM, whiteSpace: 'nowrap' }}>
          {t('structuredNote.loading.title')}
        </Text>
      </div>
    </div>
  );
}

/**
 * Relative wrapper that can show {@link StructureNoteLoadingOverlay} over its children.
 */
export function StructuredNoteFieldShell({
  children,
  isLoading,
  id,
  fieldRef,
}: {
  children: React.ReactNode;
  isLoading: boolean;
  id?: string;
  fieldRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      id={id}
      ref={fieldRef}
      style={{ position: 'relative', width: '100%' }}
      aria-busy={isLoading}
    >
      {children}
      <StructureNoteLoadingOverlay visible={isLoading} />
    </div>
  );
}
